import { Job, SupabaseJob } from '@applicator/shared'

import { supabase } from '../../../lib/supabase'

// Render toolbar for selected jobs
export function renderToolbar(
  selectedRows: Set<number>,
  selectedData: Job[],
  supabaseJobs: SupabaseJob[],
  analyzingJobIds: Set<string>,
  relevanceFilter: string,
  handlePrepareClickWrapper: (jobIds: string[]) => void,
  handleBulkAnalyzeRelevance: () => Promise<void>,
  setReviewRulesJob: React.Dispatch<React.SetStateAction<{ job: Job; supabaseJob: SupabaseJob } | null>>,
  loadJobs: () => Promise<void>
) {
  // Ensure selectedData is always an array
  const safeSelectedData = Array.isArray(selectedData) ? selectedData : []
  
  const selectedJobsWithoutRelevance = safeSelectedData.filter(job => {
    const supabaseJob = supabaseJobs.find(j => j.id === job.id)
    return supabaseJob && (supabaseJob.relevance_verdict === null || supabaseJob.relevance_verdict === undefined)
  })
  
  const selectedNonRelevantJobs = safeSelectedData.filter(job => {
    const supabaseJob = supabaseJobs.find(j => j.id === job.id)
    return supabaseJob && supabaseJob.relevance_verdict === 'No Fit'
  })

  const selectedRelevantJobs = safeSelectedData.filter(job => {
    const supabaseJob = supabaseJobs.find(j => j.id === job.id)
    return supabaseJob && supabaseJob.relevance_verdict === 'Fit'
  })

  if (selectedRows.size === 0) return null

  const handleMarkAsRelevant = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return

      for (const job of selectedNonRelevantJobs) {
        if (job.id) {
          await supabase.rpc('update_job_relevance', {
            p_user_id: user.id,
            p_job_id: job.id,
            p_is_relevant: true
          })
        }
      }

      // Refresh jobs
      loadJobs()
    } catch (err) {
      console.error('Error marking jobs as relevant:', err)
    }
  }

  const handleReviewRules = () => {
    if (selectedNonRelevantJobs.length > 0) {
      const firstJob = selectedNonRelevantJobs[0]
      const supabaseJob = supabaseJobs.find(j => j.id === firstJob.id)
      if (supabaseJob) {
        setReviewRulesJob({ job: firstJob, supabaseJob })
      }
    }
  }

  return (
    <div className="flex items-center justify-end gap-2 px-4 py-2 bg-surface-subtle border-b border-border-default">
      {relevanceFilter === 'relevant' && selectedRelevantJobs.length > 0 && (
        <button
          onClick={() => {
            const jobIds = selectedRelevantJobs.map(job => job.id || '').filter(Boolean)
            handlePrepareClickWrapper(jobIds)
          }}
          className="px-3 py-1.5 text-sm font-medium text-text-inverse bg-brand-primary rounded-md hover:bg-brand-primary-hover transition-colors flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <span>Prepare Application ({selectedRelevantJobs.length})</span>
        </button>
      )}
      
      {selectedJobsWithoutRelevance.length > 0 && (
        <button
          onClick={handleBulkAnalyzeRelevance}
          disabled={analyzingJobIds.size > 0}
          className="px-3 py-1.5 text-sm font-medium text-info bg-info-subtle border border-info/20 rounded-md hover:bg-info-subtle disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          title={`Analyze relevance for ${selectedJobsWithoutRelevance.length} selected job(s)`}
        >
          {analyzingJobIds.size > 0 ? (
            <>
              <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span>Analyzing ({analyzingJobIds.size})</span>
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <span>Analyze Relevance ({selectedJobsWithoutRelevance.length})</span>
            </>
          )}
        </button>
      )}
      
      {selectedNonRelevantJobs.length > 0 && (
        <>
          <button
            onClick={handleMarkAsRelevant}
            className="px-3 py-1.5 text-sm font-medium text-brand-primary-strong bg-brand-primary-subtle border border-brand-primary/20 rounded-md hover:bg-brand-primary-subtle flex items-center gap-2"
            title={`Mark ${selectedNonRelevantJobs.length} job(s) as relevant`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span>Mark as Relevant ({selectedNonRelevantJobs.length})</span>
          </button>
          <button
            onClick={handleReviewRules}
            className="px-3 py-1.5 text-sm font-medium text-text-secondary bg-surface-muted border border-border-default rounded-md hover:bg-surface-subtle flex items-center gap-2"
            title="Review and update matching rules based on this job"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
            <span>Review Matching Rules</span>
          </button>
        </>
      )}
    </div>
  )
}
